import type { FC } from 'react';
import { Box, Divider, Stack } from '@mui/material';
import { differenceInCalendarMonths, isSameMonth } from 'date-fns';

import { Month } from './Month';
import type { DateRangePickerProps } from './types';
import { useDateRangePicker } from './useDateRangePicker';
import { MARKERS } from './utils';

export const DateRangePicker: FC<DateRangePickerProps> = ({
  initialDateRange,
  minDate,
  maxDate,
  locale,
  onChange,
  hideOutsideMonthDays,
}) => {
  const {
    dateRange,
    minDate: minValidDate,
    maxDate: maxValidDate,
    firstMonth,
    secondMonth,
    handleSetFirstMonth,
    handleSetSecondMonth,
    helpers,
    handlers,
  } = useDateRangePicker({ initialDateRange, minDate, maxDate, onChange });

  const canNavigateCloser =
    differenceInCalendarMonths(secondMonth, firstMonth) >= 2;
  const canNavigateBackFirst = !isSameMonth(firstMonth, minValidDate);
  const canNavigateForwardSecond = !isSameMonth(secondMonth, maxValidDate);

  const commonProps = {
    dateRange,
    minDate: minValidDate,
    maxDate: maxValidDate,
    helpers,
    handlers,
    locale,
    hideOutsideMonthDays,
  };

  return (
    <Box
      sx={{
        borderRadius: '16px',
        backgroundColor: '#fff',
        overflow: 'hidden',
        display: 'flex',
      }}
    >
      <Stack direction="column" sx={{ flexGrow: 1 }}>
        <Stack direction="row">
          <Box>
            <Month
              {...commonProps}
              currentDate={firstMonth}
              setMonth={handleSetFirstMonth}
              navState={[canNavigateBackFirst, canNavigateCloser]}
              marker={MARKERS.FIRST_MONTH}
            />
          </Box>

          <Divider orientation="vertical" flexItem />

          <Box>
            <Month
              {...commonProps}
              currentDate={secondMonth}
              setMonth={handleSetSecondMonth}
              navState={[canNavigateCloser, canNavigateForwardSecond]}
              marker={MARKERS.SECOND_MONTH}
            />
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};
